import { Button, ConfirmDialog, Field, TextOutput } from 'Components/Core';
import { Grid, GridFieldSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { hasNonEmptyValue } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import deepEquals from 'Lib/Object/deepEquals';
import { orderBy } from 'lodash';
import React, { useCallback, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { MdRemoveCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { pipe } from 'rxjs';
import { NamePrefixModifier, withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { MoreInformationDialogSection } from '../Sections/MoreInformationDialogSection';
import { AsImportedToRelativeCompactionTable } from './AsImportedToRelativeCompactionTable';

let AsAssignedValuesResultsSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, prefixName }) => {
  const [showMoreInformation, setShowMoreInformation] = useState(false);
  const [index, setIndex] = useState<number>(0); //Stores the index of the currently clicked Assigned Value Test
  const amddTestId = useSelector(pipe(createWorksheetSelector!('Id')));
  const dispatch = useDispatch();

  /**
   * A collection of Assigned Value Tests
   */
  const assignedValueTests = useSelector(
    pipe(
      createWorksheetSelector!('AssignedValueTests'),
      (tests: any) =>
        tests?.map((t: any) => ({
          DateAssigned: t.DateAssigned,
          AssignedMaximumDryDensity: t.AssignedMaximumDryDensity,
          AssignedOptimumMoistureContent: t.AssignedOptimumMoistureContent,
          TimesUsed: t.TimesUsed,
          TestPortionId: t.TestPortionId,
        }))
    ),
    deepEquals
  );

  /**
   * Onclick to remove an assigned value test
   */
  const removeTest = (id: any) => {
    dispatch(
      actions.removeArrayEntry({ name: prefixName!('AssignedValueTests'), index: FindIndexByTestId(id), sync: true })
    );
  };

  const showMoreInformationDialog = (id: any) => {
    setIndex(FindIndexByTestId(id));
    setShowMoreInformation(true);
  };

  /**
   * Finds the index of a test by the given Test ID
   */
  const FindIndexByTestId = (id: any) => {
    for (let i = 0; i < assignedValueTests.length; i++) {
      if (assignedValueTests[i].TestPortionId === id) {
        return i;
      }
    }
    return 0;
  };

  /**
   * It is necessary to wrap this in a callback because the re-import of mdd source entities on an assigned value test,
   * results in a sync of the worksheet which in turn re-renders this grid. The dialog would be open during this time
   * which would result in it re-loading the mdd source entities again - it ultimatly does it twice during a re-import instead
   * of once.
   */
  const onClose = useCallback(() => {
    setShowMoreInformation(false);
  }, []);

  const gridRows = orderBy(assignedValueTests, ['DateAssigned'], ['desc'])?.map((s: any) => {
    return [
      <Field
        input={<TextOutput />}
        value={s?.AssignedMaximumDryDensity?.value}
        format={{ uom: 't/m³', nearest: 0.01 }}
      />,
      <Field input={<TextOutput />} value={s?.AssignedOptimumMoistureContent} format={{ nearest: 0.5 }} />,
      <Field input={<TextOutput />} value={s?.DateAssigned} format={{ type: FormatTypes.DateTime }} />,
      <Button subtle onClick={() => showMoreInformationDialog(s.TestPortionId)} primary>
        <FaInfoCircle size="1.70em" />
      </Button>,
      <ConfirmDialog
        confirmTitle="Delete Assigned Value Test"
        confirmText={() => GetConfirmTextDialog(s?.TimesUsed, FindIndexByTestId(s.TestPortionId))}
        confirmButtonText="Delete"
        confirmCancelButtonText="Cancel"
      >
        <Button subtle onClick={() => removeTest(s.TestPortionId)} error>
          <MdRemoveCircle size="2em" />
        </Button>
      </ConfirmDialog>,
    ];
  });

  return (
    <>
      <GridFieldSection name="AssignedValueTests" visible={hasNonEmptyValue(gridRows)}>
        <Grid
          colHeaders={['Assigned Maximum Dry Density (t/m³)', 'Assigned Optimum Moisture Content (%)', 'Date Assigned']}
          data={gridRows}
        />
      </GridFieldSection>
      <NamePrefixModifier appendNamePrefix={`AssignedValueTests[${index}]`}>
        <MoreInformationDialogSection AmddTestId={amddTestId} visible={showMoreInformation} onClose={onClose} />
      </NamePrefixModifier>
    </>
  );
};

AsAssignedValuesResultsSection = withInjectedProps(AsAssignedValuesResultsSection);

export { AsAssignedValuesResultsSection };
export default AsAssignedValuesResultsSection;

const GetConfirmTextDialog = (timesUsed: number, index: number) => {
  let confirmText = (
    <>
      <div>Are you sure you want to delete this Assigned Value test?</div>
      <br />
      <div>This action cannot be undone.</div>
    </>
  );
  let TestUsage;
  if (timesUsed > 0) {
    TestUsage = (
      <>
        <br />
        <div>
          This Assigned Value test is used on the following
          {timesUsed === 1 ? ' test' : ' tests'}.
        </div>
        <br />
        <NamePrefixModifier appendNamePrefix={`AssignedValueTests[${index}]`}>
          <AsImportedToRelativeCompactionTable />
        </NamePrefixModifier>
      </>
    );
  } else {
    TestUsage = (
      <>
        <br />
        <div>This Assigned Value test has not been imported to any tests yet.</div>
      </>
    );
  }

  return (
    <>
      {confirmText}
      {TestUsage}
    </>
  );
};
